import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, startOfDay, endOfDay, subDays, subHours, subWeeks, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { fr as frLocale, es as esLocale, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Calendar } from '../ui/calendar';

const LOCALES = { fr: frLocale, es: esLocale, en: enUS };

export const buildPreset = (key) => {
    const now = new Date();
    switch (key) {
        case 'today': return { start: startOfDay(now), end: now };
        case 'yesterday': return { start: startOfDay(subDays(now, 1)), end: endOfDay(subDays(now, 1)) };
        case 'last1h': return { start: subHours(now, 1), end: now };
        case 'last3h': return { start: subHours(now, 3), end: now };
        case 'last6h': return { start: subHours(now, 6), end: now };
        case 'last12h': return { start: subHours(now, 12), end: now };
        case 'last24h': return { start: subHours(now, 24), end: now };
        case 'last3d': return { start: startOfDay(subDays(now, 3)), end: now };
        case 'last7d': return { start: startOfDay(subDays(now, 7)), end: now };
        case 'last14d': return { start: startOfDay(subDays(now, 14)), end: now };
        case 'last30d': return { start: startOfDay(subDays(now, 30)), end: now };
        case 'thisWeek': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: now };
        case 'lastWeek': return { start: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), end: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) };
        case 'last2w': return { start: startOfDay(subWeeks(now, 2)), end: now };
        case 'last4w': return { start: startOfDay(subWeeks(now, 4)), end: now };
        case 'thisMonth': return { start: startOfMonth(now), end: now };
        case 'lastMonth': return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
        case 'last3m': return { start: startOfDay(subMonths(now, 3)), end: now };
        case 'last6m': return { start: startOfDay(subMonths(now, 6)), end: now };
        default: return { start: startOfDay(subDays(now, 7)), end: now };
    }
};

const QUICK = ['today', 'yesterday', 'last24h', 'last7d', 'last14d', 'last30d', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'];
const TABS = {
    hours: ['last1h', 'last3h', 'last6h', 'last12h', 'last24h'],
    days: ['today', 'yesterday', 'last3d', 'last7d', 'last14d', 'last30d'],
    weeks: ['thisWeek', 'lastWeek', 'last2w', 'last4w'],
    months: ['thisMonth', 'lastMonth', 'last3m', 'last6m'],
};

export const TimeRangePicker = ({ value, onChange }) => {
    const { t, i18n } = useTranslation();
    const locale = LOCALES[(i18n.language || 'en').slice(0, 2)] || enUS;
    const dfmt = (i18n.language || 'en').startsWith('en') ? 'MMM d' : 'd MMM';
    const [open, setOpen] = useState(false);
    const [range, setRange] = useState();
    const [fromTime, setFromTime] = useState('00:00');
    const [toTime, setToTime] = useState('23:59');

    const pillLabel = value?.labelKey
        ? t(`timeRange.presets.${value.labelKey}`)
        : value?.start
            ? `${format(value.start, dfmt, { locale })} – ${format(value.end, dfmt, { locale })}`
            : t('timeRange.presets.last7d');

    const pick = (key) => {
        onChange({ ...buildPreset(key), labelKey: key });
        setOpen(false);
    };

    const applyCustom = () => {
        if (!range?.from) return;
        const from = new Date(range.from);
        const to = new Date(range.to || range.from);
        const [fh, fm] = fromTime.split(':').map(Number);
        const [th, tm] = toTime.split(':').map(Number);
        from.setHours(fh || 0, fm || 0, 0, 0);
        to.setHours(th ?? 23, tm ?? 59, 59, 999);
        if (to <= from) return;
        onChange({ start: from, end: to, labelKey: null });
        setOpen(false);
    };

    const PresetBtn = ({ k, active }) => (
        <Button
            variant={active ? 'default' : 'outline'}
            size="sm"
            className="justify-center h-auto min-h-9 py-1.5 px-2 text-sm whitespace-normal leading-tight text-center"
            onClick={() => pick(k)}
            data-testid={`time-preset-${k}`}
        >
            {t(`timeRange.presets.${k}`)}
        </Button>
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto justify-start rounded-full px-4" data-testid="time-range-pill">
                    <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
                    <span className="truncate">{pillLabel}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[92vw] max-w-[560px] p-3" data-testid="time-range-popover">
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('timeRange.quick')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-3">
                    {QUICK.map(k => <PresetBtn key={k} k={k} active={value?.labelKey === k} />)}
                </div>
                <Tabs defaultValue="days">
                    <TabsList className="w-full grid grid-cols-5 h-9">
                        <TabsTrigger value="hours" data-testid="time-tab-hours" className="text-xs sm:text-sm">{t('timeRange.tabs.hours')}</TabsTrigger>
                        <TabsTrigger value="days" data-testid="time-tab-days" className="text-xs sm:text-sm">{t('timeRange.tabs.days')}</TabsTrigger>
                        <TabsTrigger value="weeks" data-testid="time-tab-weeks" className="text-xs sm:text-sm">{t('timeRange.tabs.weeks')}</TabsTrigger>
                        <TabsTrigger value="months" data-testid="time-tab-months" className="text-xs sm:text-sm">{t('timeRange.tabs.months')}</TabsTrigger>
                        <TabsTrigger value="custom" data-testid="time-tab-custom" className="text-xs sm:text-sm">{t('timeRange.tabs.custom')}</TabsTrigger>
                    </TabsList>
                    {Object.entries(TABS).map(([tab, keys]) => (
                        <TabsContent key={tab} value={tab} className="mt-3">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                {keys.map(k => <PresetBtn key={k} k={k} active={value?.labelKey === k} />)}
                            </div>
                        </TabsContent>
                    ))}
                    <TabsContent value="custom" className="mt-3">
                        <div className="flex flex-col items-center gap-3">
                            <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} locale={locale} data-testid="time-custom-calendar" />
                            <div className="flex items-center gap-2 w-full">
                                <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                                <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)}
                                    className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                                    aria-label={t('timeRange.startTime')} data-testid="time-custom-from" />
                                <span className="text-muted-foreground text-sm">{t('timeRange.to')}</span>
                                <input type="time" value={toTime} onChange={e => setToTime(e.target.value)}
                                    className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                                    aria-label={t('timeRange.endTime')} data-testid="time-custom-to" />
                            </div>
                            <Button className="w-full" onClick={applyCustom} disabled={!range?.from} data-testid="time-custom-apply">
                                {t('timeRange.apply')}
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>
            </PopoverContent>
        </Popover>
    );
};
